import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { useAdminAuth } from "@/context/AdminAuthContext";

function getErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return "Couldn't sign in. Please check your email and password.";
}

export default function AdminLogin() {
  const { admin, login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (admin) return <Navigate to="/admin" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-6">
      <SEO title="Admin Login" description="Blueprint4Joy® admin sign in." path="/admin/login" noindex />
      <div data-testid="admin-login-page" className="w-full max-w-[420px]">
        <div className="text-center mb-10">
          <span className="font-display text-3xl tracking-tight text-[#1C1917]">
            Blueprint<span className="text-[#FF5A4F]">4</span>Joy
            <span className="text-[#159E9A] align-super text-[12px] ml-1">®</span>
          </span>
          <p className="mt-3 text-sm text-[#57534E]">Sign in to manage This Curious Life.</p>
        </div>
        <form onSubmit={onSubmit} data-testid="admin-login-form" className="space-y-4">
          <div>
            <label className="block text-xs tracking-wide uppercase text-[#57534E] mb-1.5">Email</label>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full rounded-xl px-4 py-3 text-sm bg-white border border-[#E7E5E4] outline-none focus:border-[#159E9A] transition"
            />
          </div>
          <div>
            <label className="block text-xs tracking-wide uppercase text-[#57534E] mb-1.5">Password</label>
            <input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="w-full rounded-xl px-4 py-3 text-sm bg-white border border-[#E7E5E4] outline-none focus:border-[#159E9A] transition"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FF5A4F] text-white py-3 text-sm font-medium hover:bg-[#e8463c] transition disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={16} /> Signing in…
              </>
            ) : (
              "Sign in"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
